
import { useState, useEffect } from 'react';

interface ScrollProgressIndicatorProps {
  className?: string;
  showPercentage?: boolean;
}

const ScrollProgressIndicator = ({
  className = '',
  showPercentage = false,
}: ScrollProgressIndicatorProps) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const calculateScrollProgress = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;

      const sections = [
        '#program-overview',
        '#specializations',
        '#fees-emi',
        '#success-stories',
      ];

      let totalSectionHeight = 0;
      let scrolledSectionHeight = 0;

      sections.forEach((selector) => {
        const element = document.querySelector(selector);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + scrollTop;
          const elementBottom = elementTop + rect.height;

          totalSectionHeight += rect.height;

          if (scrollTop + windowHeight > elementTop) {
            const visibleHeight = Math.min(
              scrollTop + windowHeight - elementTop,
              rect.height
            );
            scrolledSectionHeight += visibleHeight;
          }
        }
      });

      const progress = totalSectionHeight > 0
        ? Math.min((scrolledSectionHeight / totalSectionHeight) * 100, 100)
        : 0;

      setScrollProgress(progress);
      setIsVisible(scrollTop > 100);
    };

    window.addEventListener('scroll', calculateScrollProgress);
    calculateScrollProgress();

    return () => window.removeEventListener('scroll', calculateScrollProgress);
  }, []);

  if (!isVisible) return null;

  return (
    <div className={`fixed top-20 left-0 right-0 z-nav ${className}`}>
      <div className="relative h-1 bg-muted">
        <div
          className="absolute top-0 left-0 h-full bg-primary transition-all duration-300 ease-out"
          style={{ width: `${scrollProgress}%` }}
        >
          {showPercentage && scrollProgress > 10 && (
            <div className="absolute right-0 top-full mt-1 bg-primary text-primary-foreground text-xs font-semibold px-2 py-1 rounded-md shadow-sm whitespace-nowrap">
              {Math.round(scrollProgress)}%
            </div>
          )}
        </div>
      </div>

      {/* Section Markers */}
      <div className="absolute top-0 left-0 right-0 h-1 flex justify-between pointer-events-none">
        {[25, 50, 75].map((position) => (
          <div
            key={position}
            className="w-0.5 h-full bg-border"
            style={{ marginLeft: `${position}%` }}
          />
        ))}
      </div>
    </div>
  );
};

export default ScrollProgressIndicator;